import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Mail, Lock, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/lib/context';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Provider Login Page
 */
const ProviderLoginPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);

  // i18n labels
  const labels = {
    en: {
      title: 'Provider Sign In',
      subtitle: 'Access your provider dashboard',
      email: 'Email Address',
      emailPlaceholder: 'you@example.com',
      password: 'Password',
      passwordPlaceholder: 'Your password',
      signIn: 'Sign In',
      signing: 'Signing in...',
      noAccount: "Don't have a provider account?",
      register: 'Register here',
      back: 'Back to homepage',
      loginSuccess: 'Login successful!',
      enterBoth: 'Please enter both email and password'
    },
    et: {
      title: 'Pakkuja sisselogimine',
      subtitle: 'Logi sisse oma pakkuja kontole',
      email: 'E-posti aadress',
      emailPlaceholder: 'sina@näide.ee',
      password: 'Parool',
      passwordPlaceholder: 'Sinu parool',
      signIn: 'Logi sisse',
      signing: 'Sisselogimine...',
      noAccount: 'Pole veel pakkuja kontot?',
      register: 'Registreeru siin',
      back: 'Tagasi avalehele',
      loginSuccess: 'Sisselogimine õnnestus!',
      enterBoth: 'Palun sisesta e-post ja parool'
    },
    fi: {
      title: 'Palveluntarjoajan kirjautuminen',
      subtitle: 'Käytä palveluntarjoajan hallintapaneelia',
      email: 'Sähköpostiosoite',
      emailPlaceholder: 'sinä@esimerkki.fi',
      password: 'Salasana',
      passwordPlaceholder: 'Salasanasi',
      signIn: 'Kirjaudu sisään',
      signing: 'Kirjaudutaan...',
      noAccount: 'Ei vielä palveluntarjoajatiliä?',
      register: 'Rekisteröidy täällä',
      back: 'Takaisin etusivulle',
      loginSuccess: 'Kirjautuminen onnistui!',
      enterBoth: 'Syötä sekä sähköposti että salasana'
    }
  };
  const l = labels[language] || labels.en;

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError(l.enterBoth);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/providers/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Invalid credentials');
      }
      
      // Store session info
      if (data.provider_id) {
        localStorage.setItem('provider_id', data.provider_id);
        if (data.session_token) {
          localStorage.setItem('provider_session', data.session_token);
        }
      }

      toast.success(l.loginSuccess);
      navigate('/provider/dashboard');
      
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-orange-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6">
            <h1 className="text-3xl font-bold text-orange-600">KEIKKA</h1>
          </Link>
          <h2 className="text-2xl font-bold text-gray-900">{l.title}</h2>
          <p className="text-gray-600 mt-2">
            {l.subtitle}
          </p>
        </div>

        <Card>
          <CardContent className="pt-6">
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="email">{l.email}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input 
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={l.emailPlaceholder}
                    className="pl-10"
                    data-testid="login-email"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="password">{l.password}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input 
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={l.passwordPlaceholder}
                    className="pl-10"
                    data-testid="login-password"
                  />
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {l.signing}
                  </>
                ) : (
                  <>
                    {l.signIn}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer links */}
        <div className="text-center mt-6 space-y-2">
          <p className="text-gray-600">
            {l.noAccount}{' '}
            <Link to="/provider/register" className="text-orange-600 hover:underline font-medium">
              {l.register}
            </Link>
          </p>
          <Link to="/" className="text-gray-500 hover:text-gray-700 text-sm">
            ← {l.back}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ProviderLoginPage;
